/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { match } from "@formatjs/intl-localematcher";
import Negotiator from "negotiator";
import { NextResponse, type NextRequest } from "next/server";

import { LocaleCookieName } from "../types/locale.types";
import { getLocalePrefixMode } from "../utils/locale-path.utils";
import {
	checkPathnameHasLocale,
	langToLocale,
	pathnameStartsWithLocale,
	pathnameToLocale,
	removePathnameLocale,
	replaceUnsupportedLocale,
} from "../utils/locale.utils";

const getCurrentLocale = (headers: Negotiator.Headers, locales: string[], defaultLocale: string): string => {
	if (!headers) return defaultLocale;
	try {
		const languages = new Negotiator({ headers }).languages();
		return match(languages, locales, defaultLocale);
	} catch {
		//Failed to determine locale from headers
		return defaultLocale;
	}
};

// Cache for locale mapping regexes to avoid reconstruction on every request
const localeMappingRegexCache = new Map<string, RegExp>();

const getLocaleMappingRegex = (unsupportedLocales: string[]): RegExp => {
	const cacheKey = unsupportedLocales.join("|");
	let regex = localeMappingRegexCache.get(cacheKey);
	if (!regex) {
		regex = new RegExp(`^/(${cacheKey})(/?$|/.+)`, "i");
		localeMappingRegexCache.set(cacheKey, regex);
	}
	return regex;
};

/**
 * Processes locale handling for incoming requests, including:
 * - Custom locale mapping for unsupported ISO 639 codes
 * - Locale validation and redirection
 * - Language parameter handling
 * - Accept-Language header negotiation
 *
 * @param request - The incoming Next.js request
 * @param locales - Array of supported locale codes (e.g., ["en-US", "fr", "de"])
 * @param defaultLocale - Default locale to use as fallback
 * @param unsupportedLocaleMapping - Optional mapping of unsupported locale codes to supported ones.
 *   Example: `{ "en": "en-US", "zh": "zh-CN" }`. Define this in your site's `i18n/config.ts`
 *   to automatically redirect legacy/unsupported locales.
 *   Values must be type-safe: `Record<string, typeof locales[number]>`.
 *
 * The locale prefix mode (`always` | `as-needed` | `never`) is read from
 * `process.env.NEXT_PUBLIC_LOCALE_MODE` (defaults to `"always"`).
 *
 * @example
 * // i18n/config.ts
 * export const unsupportedLocaleMapping: Record<string, typeof locales[number]> = {
 *   en: "en-US",
 *   zh: "zh-CN",
 * };
 *
 * // middleware.ts
 * const localeRes = processLocale(request, locales, defaultLocale, unsupportedLocaleMapping);
 */
export function processLocale(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): Response | NextResponse | void {
	const localePrefix = getLocalePrefixMode();
	const { pathname = "/" } = request.nextUrl;

	// Handle "as-needed" locale prefix mode
	// In this mode, the default locale has no URL prefix while other locales do
	if (localePrefix === "as-needed") {
		return processLocaleAsNeeded(request, locales, defaultLocale, unsupportedLocaleMapping);
	}

	// Handle "never" locale prefix mode
	// In this mode, no locale prefixes are used in URLs (domain-based routing)
	if (localePrefix === "never") {
		return processLocaleNever(request, locales, defaultLocale, unsupportedLocaleMapping);
	}

	// Default "always" mode - all locales have prefixes
	// Check for custom locale mapping first (e.g., /en/* -> /en-US/*)
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const match = pathname.match(localeRegex);

		if (match) {
			const unsupportedLocale = match[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];

			if (targetLocale && locales.includes(targetLocale)) {
				const rest = match[2] === "/" ? "" : match[2];
				request.nextUrl.pathname = `/${targetLocale}${rest}`;
				return Response.redirect(request.nextUrl, 308);
			}
		}
	}

	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		/**
		 * The pathname has a locale but it's not one of the app's supported locales,
		 *  update the pathname to default locale and redirect.
		 */
		request.nextUrl.pathname = replacedUnsupportedLocale;
		return Response.redirect(request.nextUrl);
	}

	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);

	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam?.length) {
		const localeFromParam = langToLocale(langParam, locales);
		/**
		 * The lang param is present and valid but the pathname doesn't start with locale,
		 *  update the pathname to new locale and redirect.
		 */
		if (typeof localeFromParam === "string" && !pathnameStartsWithLocale(pathname, localeFromParam)) {
			if (pathnameHasLocale) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = `/${localeFromParam}${newPathname}`;
			} else {
				request.nextUrl.pathname = `/${localeFromParam}${pathname}`;
			}
			request.nextUrl.searchParams.delete("lang");
			return Response.redirect(request.nextUrl);
		}
	}

	if (pathnameHasLocale) {
		/**
		 * The pathname has locale but pathname locale case doesn't match,
		 *  update the pathname to correct locale case and redirect.
		 */
		const localeFromPath = pathnameHasLocale && pathnameToLocale(pathname, locales);
		if (typeof localeFromPath === "string" && !pathnameStartsWithLocale(pathname, localeFromPath)) {
			const newPathname = removePathnameLocale(pathname, locales);
			request.nextUrl.pathname = `/${localeFromPath}${newPathname}`;
			return Response.redirect(request.nextUrl);
		}
	} else {
		/**
		 * The lang param isn't present and there isn't a locale in the pathname,
		 *  update the pathname to have a locale from the language headers, and then redirect.
		 */
		const requestHeaders = new Headers(request.headers);
		const languageHeaders: Negotiator.Headers = {
			"accept-charset": requestHeaders.get("accept-charset") || undefined,
			"accept-encoding": requestHeaders.get("accept-encoding") || undefined,
			"accept-language": requestHeaders.get("accept-language") || undefined,
		};
		const locale = getCurrentLocale(languageHeaders, locales, defaultLocale);
		request.nextUrl.pathname = `/${locale}${pathname}`;
		// Use manual Response instead of Response.redirect() to add Vary header.
		// Response.redirect() returns immutable headers.
		return new Response(null, {
			status: 302,
			headers: {
				Location: request.nextUrl.toString(),
				Vary: "Accept-Language",
			},
		});
	}

	// e.g. incoming request is /products
	// The new URL is now /en-US/products
}

/**
 * Processes locale for "as-needed" mode where the default locale has no URL prefix
 *
 * Behaviors:
 * - /en-US/page (default locale with prefix) -> Redirect to /page
 * - /page (no locale) -> Rewrite internally to /en-US/page
 * - /de/page (non-default locale) -> Pass through normally
 */
function processLocaleAsNeeded(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): Response | NextResponse | void {
	const { pathname = "/" } = request.nextUrl;

	// Check for custom locale mapping first (e.g., /en/* -> /en-US/*)
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const mappingMatch = pathname.match(localeRegex);

		if (mappingMatch) {
			const unsupportedLocale = mappingMatch[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];

			if (targetLocale && locales.includes(targetLocale)) {
				const rest = mappingMatch[2] === "/" ? "" : mappingMatch[2];
				// If mapping to default locale, redirect without prefix
				if (targetLocale === defaultLocale) {
					request.nextUrl.pathname = rest || "/";
					return Response.redirect(request.nextUrl, 308);
				}
				request.nextUrl.pathname = `/${targetLocale}${rest}`;
				return Response.redirect(request.nextUrl, 308);
			}
		}
	}

	// Layer 2: catch globally-known locales that aren't supported by this site
	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		// Map to default locale — in as-needed mode, default has no prefix
		const rest = removePathnameLocale(replacedUnsupportedLocale, [defaultLocale]);
		request.nextUrl.pathname = rest || "/";
		return Response.redirect(request.nextUrl, 308);
	}

	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);

	// Handle lang query parameter
	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam?.length) {
		const localeFromParam = langToLocale(langParam, locales);
		if (typeof localeFromParam === "string") {
			request.nextUrl.searchParams.delete("lang");

			if (localeFromParam === defaultLocale) {
				// Default locale - redirect to path without prefix
				const newPathname = pathnameHasLocale ? removePathnameLocale(pathname, locales) : pathname;
				request.nextUrl.pathname = newPathname;
				return Response.redirect(request.nextUrl);
			} else {
				// Non-default locale - redirect to path with prefix
				const newPathname = pathnameHasLocale ? removePathnameLocale(pathname, locales) : pathname;
				request.nextUrl.pathname = `/${localeFromParam}${newPathname}`;
				return Response.redirect(request.nextUrl);
			}
		}
	}

	if (pathnameHasLocale) {
		const localeFromPath = pathnameToLocale(pathname, locales);

		if (typeof localeFromPath === "string") {
			// If path has default locale prefix, redirect to remove it
			if (localeFromPath.toLowerCase() === defaultLocale.toLowerCase()) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = newPathname || "/";
				return Response.redirect(request.nextUrl, 308);
			}

			// Non-default locale with prefix - fix case if needed and continue
			if (!pathnameStartsWithLocale(pathname, localeFromPath)) {
				const newPathname = removePathnameLocale(pathname, locales);
				request.nextUrl.pathname = `/${localeFromPath}${newPathname}`;
				return Response.redirect(request.nextUrl);
			}

			// Non-default locale with correct prefix - continue normally
			return;
		}
	}

	// No locale in pathname - rewrite to default locale internally
	// This keeps the URL as /page but routes to /[lang]/page with lang=defaultLocale
	const rewriteUrl = request.nextUrl.clone();
	rewriteUrl.pathname = `/${defaultLocale}${pathname}`;
	return NextResponse.rewrite(rewriteUrl);
}

export function getBestLocaleFromRequest(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): string {
	const langParam = request.nextUrl.searchParams.get("lang")?.trim();
	if (langParam && /^[a-zA-Z0-9_-]{1,20}$/.test(langParam)) {
		// Normalize underscore-format codes from partner sites (e.g. pt_BR → pt-BR)
		const normalizedLangParam = langParam.replace(/_/g, "-");
		if (locales.includes(normalizedLangParam)) return normalizedLangParam;
		if (unsupportedLocaleMapping) {
			const mapped = unsupportedLocaleMapping[normalizedLangParam.toLowerCase()];
			if (mapped && locales.includes(mapped)) return mapped;
		}
	}

	// The cookie value is intentionally left unchanged — it is the source of truth for the
	// user's expressed locale preference. The middleware maps it to a supported locale for
	// routing; it is the app's responsibility to render content for the resolved locale.
	const epicLangCookie = request.cookies.get(LocaleCookieName)?.value;
	if (epicLangCookie) {
		// Normalize underscore-format codes from partner sites (e.g. zh_CN → zh-CN)
		const normalizedCookie = epicLangCookie.replace(/_/g, "-");
		if (locales.includes(normalizedCookie)) return normalizedCookie;
		if (unsupportedLocaleMapping) {
			const mapped = unsupportedLocaleMapping[normalizedCookie.toLowerCase()];
			if (mapped && locales.includes(mapped)) return mapped;
		}
	}

	const requestHeaders = new Headers(request.headers);
	const languageHeaders: Negotiator.Headers = {
		"accept-charset": requestHeaders.get("accept-charset") || undefined,
		"accept-encoding": requestHeaders.get("accept-encoding") || undefined,
		"accept-language": requestHeaders.get("accept-language") || undefined,
	};
	const resolved = getCurrentLocale(languageHeaders, locales, defaultLocale);
	return resolved || defaultLocale;
}

/**
 * Processes locale for "never" mode where no locale prefixes appear in URLs
 * Useful for domain-based routing where each domain serves one locale
 */
function processLocaleNever(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	unsupportedLocaleMapping?: Record<string, string>,
): Response | NextResponse | void {
	const { pathname = "/" } = request.nextUrl;

	// Check for unsupported locale prefix (e.g., /pt/* on a site that doesn't support pt)
	// Strip the prefix and redirect; set ?lang to the mapped locale if it's not the default
	if (unsupportedLocaleMapping && Object.keys(unsupportedLocaleMapping).length > 0) {
		const unsupportedLocales = Object.keys(unsupportedLocaleMapping);
		const localeRegex = getLocaleMappingRegex(unsupportedLocales);
		const mappingMatch = pathname.match(localeRegex);

		if (mappingMatch) {
			const unsupportedLocale = mappingMatch[1].toLowerCase();
			const targetLocale = unsupportedLocaleMapping[unsupportedLocale];
			const rest = mappingMatch[2] === "/" ? "" : mappingMatch[2];
			request.nextUrl.pathname = rest || "/";
			if (targetLocale && targetLocale !== defaultLocale && !request.nextUrl.searchParams.has("lang")) {
				request.nextUrl.searchParams.set("lang", targetLocale);
			}
			return Response.redirect(request.nextUrl, 308);
		}
	}

	// Layer 2: catch globally-known locales that aren't supported by this site
	const replacedUnsupportedLocale = replaceUnsupportedLocale(pathname, locales, defaultLocale);
	if (typeof replacedUnsupportedLocale === "string") {
		// Strip the unsupported locale prefix — in never mode, no locale prefixes appear in URLs
		const rest = removePathnameLocale(replacedUnsupportedLocale, [defaultLocale]);
		request.nextUrl.pathname = rest || "/";
		return Response.redirect(request.nextUrl, 308);
	}

	// If there's a locale prefix, redirect to remove it and preserve locale as ?lang param
	const pathnameHasLocale = checkPathnameHasLocale(pathname, locales);
	if (pathnameHasLocale) {
		const localeFromPath = pathnameToLocale(pathname, locales);
		const newPathname = removePathnameLocale(pathname, locales);
		request.nextUrl.pathname = newPathname || "/";
		if (typeof localeFromPath === "string" && !request.nextUrl.searchParams.has("lang")) {
			request.nextUrl.searchParams.set("lang", localeFromPath);
		}
		return Response.redirect(request.nextUrl, 308);
	}

	// Rewrite to best locale internally
	const rewriteUrl = request.nextUrl.clone();
	const bestLocale = getBestLocaleFromRequest(request, locales, defaultLocale, unsupportedLocaleMapping);
	rewriteUrl.pathname = `/${bestLocale}${pathname}`;
	return NextResponse.rewrite(rewriteUrl);
}
